import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import '../index.css';
import CustomTable, { Column } from './CustomTable';
import ProductCategoryModal from './ProductCategoryModal';
import moment from 'moment';
import apiClient from '../services/apiClient';
import EmptyState from './EmptyState';
import ProductIcon from '../assets/svgs/ProductIcon';
import ReactGA from 'react-ga4';

interface ProductCategory {
	id: number;
	name: string;
	description: string | null;
	product_count: number;
	date_created: string;
	date_updated: string;
}

export interface ProductCategoriesRef {
	showCreatePanel: () => void;
}

const ProductCategories = forwardRef<ProductCategoriesRef>((props, ref) => {
	const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
	const [selectedProductCategory, setSelectedProductCategory] = useState<ProductCategory | null>(null);
	const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');
	const [isNewProductCategory, setIsNewProductCategory] = useState(false);

	// Columns definition for CustomTable
	const columns: Column[] = [
		{
			field: 'name',
			headerName: 'Category Name',
			minWidth: 200,
			headerClassName: 'table-header',
			cellClassName: 'font-medium text-gray-900',
			flex: 2,
		},
		// {
		// 	field: 'description',
		// 	headerName: 'Description',
		// 	minWidth: 300,
		// 	headerClassName: 'table-header',
		// 	cellClassName: 'text-gray-600',
		// 	flex: 3,
		// 	renderCell: (params) => {
		// 		if (!params.value || params.value === 'null' || params.value === null) {
		// 			return '-';
		// 		}
		// 		return params.value;
		// 	},
		// },
		{
			field: 'product_count',
			headerName: 'Products',
			minWidth: 100,
			headerClassName: 'table-header',
			cellClassName: 'text-center text-blue-600 font-medium',
			flex: 1,
		},
		{
			field: 'date_created',
			headerName: 'Created',
			minWidth: 120,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500 text-sm',
			flex: 1,
			renderCell: (params) => moment(params.value).format('MMM DD, YYYY'),
		},
	];

	const fetchProductCategories = async () => {
		setFetchState('loading');
		try {
			const response = await apiClient.get('/product-categories');
			setProductCategories(response.data || []);
			setFetchState('success');
		} catch (error) {
			console.error('Error fetching product categories:', error);
			setFetchState('failed');
		}
	};

	useEffect(() => {
		fetchProductCategories();
		ReactGA.send({
			hitType: 'pageview',
			page: '/product-categories',
			title: 'Product Categories Page',
		});
	}, []);

	const handleRowClick = (param: { row: ProductCategory }) => {
		setSelectedProductCategory(param.row);
		setIsNewProductCategory(false);
		setIsEditPanelVisible(true);
	};

	const showCreateProductCategoryPanel = () => {
		setSelectedProductCategory(null);
		setIsNewProductCategory(true);
		setIsEditPanelVisible(true);
	};

	// Expose the create function to parent component
	useImperativeHandle(ref, () => ({
		showCreatePanel: showCreateProductCategoryPanel,
	}));

	const handleProductCategoryUpdate = (updatedProductCategory: any) => {
		// Update the product category in the list
		if (updatedProductCategory.id) {
			setProductCategories(
				productCategories.map((pc) => (pc.id === updatedProductCategory.id ? updatedProductCategory : pc))
			);
		}
	};

	const handleProductCategoryDelete = (productCategoryId: number) => {
		// Remove the product category from the list
		setProductCategories(productCategories.filter((pc) => pc.id !== productCategoryId));
		setIsEditPanelVisible(false);
		ReactGA.event({
			category: 'Product Categories',
			action: 'Deleted Product Category',
			label: `Product Category ID: ${productCategoryId}`,
		});
	};

	const hideCreateProductCategoryPanel = (productCategory: ProductCategory | null = null) => {
		const wasCreatingNew = isNewProductCategory;
		setIsEditPanelVisible(false);
		setSelectedProductCategory(null);
		setIsNewProductCategory(false);

		if (productCategory) {
			if (wasCreatingNew) {
				// This is a new product category - add it to the list with default product_count
				const newCategory = {
					...productCategory,
					product_count: productCategory.product_count || 0,
				};
				setProductCategories([...productCategories, newCategory]);
				ReactGA.event({
					category: 'Product Categories',
					action: 'Created Product Category',
					label: `Product Category: ${productCategory.name}`,
				});
			} else {
				// This is an updated product category - update it in the list
				setProductCategories(
					productCategories.map((pc) => (pc.id === productCategory.id ? productCategory : pc))
				);
				ReactGA.event({
					category: 'Product Categories',
					action: 'Updated Product Category',
					label: `Product Category ID: ${productCategory.id}`,
				});
			}
		}
	};

	if (fetchState === 'loading') {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="text-gray-500">Loading product categories...</div>
			</div>
		);
	}

	if (fetchState === 'failed') {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="text-red-500">Failed to load product categories. Please try again.</div>
			</div>
		);
	}

	return (
		<>
			<CustomTable
				columns={columns}
				objects={productCategories || []}
				sx={{ marginTop: '0' }}
				onRowClick={handleRowClick}
				fetchState={fetchState}
				initialRowsPerPage={25}
				emptyStateComponent={() => (
					<EmptyState
						icon={<ProductIcon />}
						title="No product categories yet"
						description="Create your first product category to organize products"
						handleButtonClick={showCreateProductCategoryPanel}
						buttonText="Add Category"
					/>
				)}
			/>

			<ProductCategoryModal
				isVisible={isEditPanelVisible}
				handleHidePanel={hideCreateProductCategoryPanel}
				productCategory={selectedProductCategory}
				isNewProductCategory={isNewProductCategory}
				onProductCategoryUpdate={handleProductCategoryUpdate}
				onProductCategoryDelete={handleProductCategoryDelete}
			/>
		</>
	);
});

export default ProductCategories;
